import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Lightning, SignIn, GoogleLogo } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { startGoogleSignIn } from "../lib/googleSignIn";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-ink-900 text-white flex items-center justify-center px-6 grid-bg relative" data-testid="login-page">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-cyan/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-8 text-zinc-400 hover:text-white transition-colors" data-testid="login-back-link">
          <Lightning size={18} weight="fill" className="text-neon-cyan" />
          <span className="font-display font-black tracking-tight">UNDAGROUND SHOOW</span>
        </Link>

        <div className="glass rounded-lg p-8 lg:p-10">
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-3">Admin Access</p>
          <h1 className="text-3xl font-black tracking-tighter mb-2">Run the Shoow.</h1>
          <p className="text-sm text-zinc-500 mb-8">Swap artwork, drop new audio, refresh the countdown.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-[11px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@playlist.com"
                className="w-full px-4 py-3 rounded-md bg-ink-800 border border-ink-600 text-white placeholder-zinc-600 focus:outline-none focus:border-neon-cyan/60 focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold tracking-[0.2em] text-zinc-400 uppercase mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-md bg-ink-800 border border-ink-600 text-white placeholder-zinc-600 focus:outline-none focus:border-neon-cyan/60 focus:ring-2 focus:ring-neon-cyan/20 transition-all"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="text-sm text-neon-magenta bg-neon-magenta/10 border border-neon-magenta/30 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-neon-cyan text-black font-semibold hover:bg-neon-cyanHover transition-all duration-300 shadow-[0_0_24px_rgba(0,240,255,0.3)] disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="login-submit-button"
            >
              <SignIn size={16} weight="bold" />
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <div className="h-px flex-1 bg-ink-600" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">or</span>
            <div className="h-px flex-1 bg-ink-600" />
          </div>

          <button
            type="button"
            onClick={() => startGoogleSignIn("/auth/callback")}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-white text-black font-semibold hover:bg-zinc-100 transition-all duration-300 disabled:opacity-60"
            data-testid="login-google-button"
          >
            <GoogleLogo size={18} weight="bold" />
            Continue with Google
          </button>

          <p className="mt-6 text-xs font-mono text-zinc-600 leading-relaxed">
            Admin-only access. Only allow-listed Google emails are promoted to admin.
          </p>
        </div>
      </div>
    </div>
  );
}
